import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import {
  ContentMedia,
  CourseContentType,
  CourseFormState,
  InteractiveElement,
} from '@/client/courses';
import {
  AnswerOptionType,
  ModifiedAnswerOption,
  ModifiedContentTranslations,
  ModifiedQuestion,
  QuizType,
} from '@/client/quizzes';
import { useCourseQuiz } from '@/hooks/query/course-content.hooks';
import { useSaveQuiz } from '@/hooks/query/quizzes.hooks';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentAccount } from '@/store/features/account';
import {
  convertQuestionsToArrayFormat,
  modifyCourseQuiz,
  sortLanguagesWithPriority,
  stripHtml,
} from '@/utils/helpers';
import { isRtlLanguage } from '@/utils/helpers/language.helper';
import { AxiosError } from 'axios';
import { get, set } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

type TranslationContent = ModifiedContentTranslations[string];

const copyTranslationContent = ({
  text,
  explanation,
  media,
  interactiveElement,
}: TranslationContent): TranslationContent => ({
  text,
  explanation,
  media,
  interactiveElement: interactiveElement && {
    type: interactiveElement.type,
    data: interactiveElement.data,
  },
});

const createEmptyQuestion = (languages: string[]): ModifiedQuestion =>
  ({
    clientKey: crypto.randomUUID(),
    answerOptions: [],
    translations: Object.fromEntries(
      languages.map((lang) => [lang, { text: '' }]),
    ),
    isAiCustomizationEnabled: false,
  } as ModifiedQuestion);

interface UseQuizEditorStateParams {
  index: number;
  values: CourseFormState;
  visible: boolean;
  setFieldValue: (field: string, value: any) => void;
  onHide: () => void;
}

export const useQuizEditorState = ({
  index,
  values,
  visible,
  setFieldValue,
  onHide,
}: UseQuizEditorStateParams) => {
  const { t } = useTranslation();
  const { id: courseId } = useParams();
  const toast = useToast();
  const currentAccount = useAppSelector(selectCurrentAccount);

  const [questions, setQuestions] = useState<ModifiedQuestion[]>([]);
  const [tempQuestions, setTempQuestions] = useState<ModifiedQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [showUnsavedChanges, setShowUnsavedChanges] = useState(false);
  const [itemName, setItemName] = useState('');
  const [savedItemName, setSavedItemName] = useState('');
  const [loadRevision, setLoadRevision] = useState(0);

  const quizId = values.courseContents?.[index]?.quiz?.id;
  const shouldFetch =
    !!quizId && !quizId.includes('temp') && visible && !!courseId;

  const { quiz, isLoading } = useCourseQuiz({
    courseId,
    quizId,
    enabled: shouldFetch,
    onError: (e: unknown) => {
      setQuestions([]);
      setTempQuestions([]);
      handleAxiosError(e as AxiosError, toast);
    },
  });

  const { save, isLoading: isSaving } = useSaveQuiz();

  const generateItemTitle = useCallback(() => {
    const title = values?.courseContents[index]?.title;
    return title
      ? title
      : t('content.name.your.item', { contentItem: t('content.quiz') });
  }, [values?.courseContents, index, t]);

  useEffect(() => {
    if (!visible) return;

    setCurrentQuestionIndex(0);
    setShowUnsavedChanges(false);

    const initialName =
      values.courseContents[index]?.quiz?.title || generateItemTitle();
    setItemName(initialName);
    setSavedItemName(initialName);

    const langs = values.courseLanguages || [];
    const sorted = sortLanguagesWithPriority(
      langs.map((lang: string) => languageDisplayCode[lang]),
    );
    const preferred = langs.find(
      (lang: string) => languageDisplayCode[lang] === sorted[0],
    );
    setSelectedLanguage(preferred || LanguagesEnum.EN);
  }, [visible]);

  // Read at effect time so loading does not restart when the form re-renders
  const translationLanguagesRef = useRef<string[]>([]);

  const applyQuestions = useCallback((next: ModifiedQuestion[]) => {
    setQuestions(next);
    setTempQuestions(JSON.parse(JSON.stringify(next)));
  }, []);

  useEffect(() => {
    if (!visible || !quiz) return;

    const modified = modifyCourseQuiz(quiz);
    if (!modified) return;

    // Loading owns the whole decision, including the empty case, so nothing
    // else can seed a blank question over data that has just arrived
    applyQuestions(
      modified.questions?.length
        ? modified.questions
        : [createEmptyQuestion(translationLanguagesRef.current)],
    );
    setLoadRevision((revision) => revision + 1);
    if (modified.title) {
      setItemName(modified.title);
      setSavedItemName(modified.title);
    }
  }, [quiz, visible, applyQuestions]);

  useEffect(() => {
    if (!visible || shouldFetch) return;

    applyQuestions([createEmptyQuestion(translationLanguagesRef.current)]);
    setCurrentQuestionIndex(0);
  }, [visible, shouldFetch, applyQuestions]);

  const isDirty = useMemo(
    () =>
      itemName !== savedItemName ||
      JSON.stringify(questions) !== JSON.stringify(tempQuestions),
    [questions, tempQuestions, itemName, savedItemName],
  );

  const isRtl = useMemo(
    () => isRtlLanguage(selectedLanguage as LanguagesEnum),
    [selectedLanguage],
  );

  const currentQuestion = questions[currentQuestionIndex];

  const translationKey = selectedLanguage;

  const translationLanguages = useMemo(
    () => (values.courseLanguages || []) as string[],
    [values.courseLanguages],
  );

  const availableLanguages = useMemo(
    () => (values.courseLanguages || []) as string[],
    [values.courseLanguages],
  );

  translationLanguagesRef.current = translationLanguages;

  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex >= questions.length) {
      setCurrentQuestionIndex(questions.length - 1);
    }
  }, [questions.length, currentQuestionIndex]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowUnsavedChanges(true);
    } else {
      onHide();
    }
  }, [isDirty, onHide]);

  // The API requires non-empty text on every translation it receives, and we
  // send one per content language — so check that up front rather than
  // surfacing a nested validation error from the backend.
  const validationError = useMemo(() => {
    const hasText = (translations: Record<string, { text?: string }>) =>
      translationLanguages.every((lang) =>
        stripHtml(translations?.[lang]?.text ?? '').trim(),
      );

    if (
      questions.some(
        (q) => (q.answerOptions?.filter((o) => o.isCorrect) || []).length === 0,
      )
    ) {
      return 'content.validation.oneCorrectAnswerRequired';
    }

    if (!questions.every((q) => hasText(q.translations))) {
      return 'content.validation.questionTextRequired';
    }

    if (
      !questions.every((q) =>
        (q.answerOptions ?? []).every((o) => hasText(o.translations)),
      )
    ) {
      return 'content.validation.answerTextRequired';
    }

    return null;
  }, [questions, translationLanguages]);

  const handleSave = useCallback(async () => {
    if (!courseId) return;

    if (validationError) {
      toast?.error(t('toast.error'), t(validationError));
      return;
    }

    try {
      const quizData = {
        title: itemName,
        questions: convertQuestionsToArrayFormat(
          questions,
          false,
          values.courseLanguages,
        ),
        supportedLanguages: values.courseLanguages,
        type: QuizType.TRADITIONAL,
        account:
          currentAccount?.isSystem || !currentAccount?.id
            ? null
            : currentAccount?.id,
      };

      const response = await save({
        courseId,
        quizId: values.courseContents?.[index]?.quiz?.id,
        data: quizData,
      });

      const updatedContents = [...values.courseContents];
      updatedContents[index] = {
        ...updatedContents[index],
        type: CourseContentType.QUIZ,
        quiz: response,
        position: index,
      };
      setFieldValue('courseContents', updatedContents);

      if (itemName !== savedItemName) {
        setFieldValue(`courseContents.${index}.isAutoNamed`, false);
      }

      const modified = modifyCourseQuiz(response);
      if (modified) {
        setQuestions(modified.questions || []);
        setTempQuestions(JSON.parse(JSON.stringify(modified.questions || [])));
      } else {
        setTempQuestions(JSON.parse(JSON.stringify(questions)));
      }
      setSavedItemName(itemName);

      onHide();
    } catch (e) {
      handleAxiosError(e as AxiosError, toast);
    }
  }, [
    courseId,
    questions,
    itemName,
    values,
    index,
    validationError,
    currentAccount,
    save,
    setFieldValue,
    onHide,
    toast,
    t,
  ]);

  const handleQuestionTextChange = useCallback(
    (value: string) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        set(
          updated,
          `[${currentQuestionIndex}].translations.${translationKey}.text`,
          value,
        );
        return updated;
      });
    },
    [currentQuestionIndex, translationKey],
  );

  const handleAnswerTextChange = useCallback(
    (answerIdx: number, value: string) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        set(
          updated,
          `[${currentQuestionIndex}].answerOptions[${answerIdx}].translations.${translationKey}.text`,
          value,
        );
        return updated;
      });
    },
    [currentQuestionIndex, translationKey],
  );

  const handleToggleCorrect = useCallback(
    (answerIdx: number) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const option = updated[currentQuestionIndex].answerOptions[answerIdx];
        option.isCorrect = !option.isCorrect;
        return updated;
      });
    },
    [currentQuestionIndex],
  );

  const handleDeleteAnswer = useCallback(
    (answerIdx: number) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        updated[currentQuestionIndex].answerOptions.splice(answerIdx, 1);
        return updated;
      });
    },
    [currentQuestionIndex],
  );

  const handleAddAnswer = useCallback(() => {
    setQuestions((prev) => {
      const updated = JSON.parse(JSON.stringify(prev));
      const newOption: ModifiedAnswerOption = {
        clientKey: crypto.randomUUID(),
        translations: Object.fromEntries(
          translationLanguages.map((lang) => [lang, { text: '' }]),
        ),
        isCorrect: false,
        position: updated[currentQuestionIndex].answerOptions.length,
        type: AnswerOptionType.STANDARD,
      };
      updated[currentQuestionIndex].answerOptions.push(newOption);
      return updated;
    });
  }, [currentQuestionIndex, translationLanguages]);

  const handleAnswerReorder = useCallback(
    (oldIdx: number, newIdx: number) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const opts = updated[currentQuestionIndex].answerOptions;
        const [moved] = opts.splice(oldIdx, 1);
        opts.splice(newIdx, 0, moved);
        opts.forEach((o: ModifiedAnswerOption, i: number) => {
          o.position = i;
        });
        return updated;
      });
    },
    [currentQuestionIndex],
  );

  const handleQuestionReorder = useCallback(
    (oldIdx: number, newIdx: number) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const [moved] = updated.splice(oldIdx, 1);
        updated.splice(newIdx, 0, moved);
        updated.forEach((q: ModifiedQuestion, i: number) => {
          q.position = i;
        });
        return updated;
      });

      if (currentQuestionIndex === oldIdx) {
        setCurrentQuestionIndex(newIdx);
      } else if (
        currentQuestionIndex > oldIdx &&
        currentQuestionIndex <= newIdx
      ) {
        setCurrentQuestionIndex((prev) => prev - 1);
      } else if (
        currentQuestionIndex < oldIdx &&
        currentQuestionIndex >= newIdx
      ) {
        setCurrentQuestionIndex((prev) => prev + 1);
      }
    },
    [currentQuestionIndex],
  );

  const handleDeleteQuestion = useCallback(
    (idx: number) => {
      setQuestions((prev) => {
        const updated = [...prev];
        updated.splice(idx, 1);
        return updated;
      });

      if (currentQuestionIndex > idx) {
        setCurrentQuestionIndex((prev) => Math.max(0, prev - 1));
      }
    },
    [currentQuestionIndex],
  );

  const handleAddQuestion = useCallback(() => {
    setQuestions((prev) => [
      ...prev,
      createEmptyQuestion(translationLanguages),
    ]);
    setCurrentQuestionIndex(questions.length);
  }, [translationLanguages, questions.length]);

  const handleCopyToLanguage = useCallback(
    (targetLangs: string[]) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        for (const q of updated) {
          const source = q.translations[translationKey];
          if (!source) continue;
          for (const lang of targetLangs) {
            q.translations[lang] = {
              ...q.translations[lang],
              ...copyTranslationContent(source),
            };
          }
          for (const opt of q.answerOptions || []) {
            const optSource = opt.translations[translationKey];
            if (!optSource) continue;
            for (const lang of targetLangs) {
              opt.translations[lang] = {
                ...opt.translations[lang],
                text: optSource.text,
              };
            }
          }
        }
        return updated;
      });
    },
    [translationKey],
  );

  const handleMediaChange = useCallback(
    (media: ContentMedia | null) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const path = `[${currentQuestionIndex}].translations.${translationKey}`;
        set(updated, `${path}.media`, media);
        if (media) set(updated, `${path}.interactiveElement`, undefined);
        return updated;
      });
    },
    [currentQuestionIndex, translationKey],
  );

  const handleInteractiveElementChange = useCallback(
    (interactiveElement: InteractiveElement | undefined) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const path = `[${currentQuestionIndex}].translations.${translationKey}`;
        set(updated, `${path}.interactiveElement`, interactiveElement);
        // A question shows either media or an interactive element, the same
        // way a slide does
        if (interactiveElement) set(updated, `${path}.media`, null);
        return updated;
      });
    },
    [currentQuestionIndex, translationKey],
  );

  const handleInteractiveElementDataChange = useCallback(
    (data: unknown) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        const path = `[${currentQuestionIndex}].translations.${translationKey}.interactiveElement`;
        const existing = get(updated, path);
        if (!existing) return prev;
        set(updated, `${path}.data`, data);
        return updated;
      });
    },
    [currentQuestionIndex, translationKey],
  );

  const handleExplanationChange = useCallback(
    (value: string) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        set(
          updated,
          `[${currentQuestionIndex}].translations.${translationKey}.explanation`,
          value,
        );
        return updated;
      });
    },
    [currentQuestionIndex, translationKey],
  );

  const handleAiCustomizationToggle = useCallback(
    (enabled: boolean) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        set(
          updated,
          `[${currentQuestionIndex}].isAiCustomizationEnabled`,
          enabled,
        );
        return updated;
      });
    },
    [currentQuestionIndex],
  );

  const handleAiInstructionsChange = useCallback(
    (value: string) => {
      setQuestions((prev) => {
        const updated = JSON.parse(JSON.stringify(prev));
        set(
          updated,
          `[${currentQuestionIndex}].aiCustomizationInstructions`,
          value,
        );
        return updated;
      });
    },
    [currentQuestionIndex],
  );

  const handleLanguageChange = useCallback((lang: string) => {
    setSelectedLanguage(lang);
  }, []);

  const handleDiscard = useCallback(() => {
    setQuestions(JSON.parse(JSON.stringify(tempQuestions)));
    setItemName(savedItemName);
    onHide();
  }, [tempQuestions, savedItemName, onHide]);

  return {
    questions,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    selectedLanguage,
    translationKey,
    availableLanguages,
    validationError,
    currentQuestion,
    isLoading,
    loadRevision,
    isSaving,
    isDirty,
    isRtl,
    showUnsavedChanges,
    setShowUnsavedChanges,
    itemName,
    setItemName,
    handleClose,
    handleSave,
    handleDiscard,
    handleQuestionTextChange,
    handleAnswerTextChange,
    handleToggleCorrect,
    handleDeleteAnswer,
    handleAddAnswer,
    handleAnswerReorder,
    handleQuestionReorder,
    handleDeleteQuestion,
    handleAddQuestion,
    handleCopyToLanguage,
    handleLanguageChange,
    handleMediaChange,
    handleInteractiveElementChange,
    handleInteractiveElementDataChange,
    handleExplanationChange,
    handleAiCustomizationToggle,
    handleAiInstructionsChange,
  };
};
